import { useState, useRef, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CheckCircle2, Camera, Upload, Loader2, MapPin, AlertCircle, X } from "lucide-react";
import type { ServiceFormTemplate } from "@shared/schema";
import { uploadMimeType } from "@/lib/upload-mime";

// Shared fillable service-form renderer (forms-on-tasks convergence).
// Used by BOTH fill surfaces with path-specific transport injected via props:
//   • the public tokenized page (client/src/pages/service-form.tsx) — member /
//     phone magic-link recipients;
//   • the vendor portal form-task detail (vendor-portal/tasks.tsx).
// It owns all fill state (checklist, photos + presigned uploads, notes,
// geolocation + bypass attestation) and produces the exact submit payload the
// shared server processor (server/services/service-form-submission.ts) expects.

type FormSection = {
  id: string;
  title: string;
  checklistItems: Array<{ id: string; label: string; photoRequired: boolean }>;
  photoSlots: Array<{ id: string; label: string; required: boolean }>;
  hasNotes: boolean;
};

export type ServiceFormSubmitPayload = {
  submitterName: string;
  notes: string;
  checklistResponses: Record<string, boolean>;
  photoUrls: Record<string, string[]>;
  latitude: number | null;
  longitude: number | null;
  dateOfService: string;
  nextServiceDate: string;
  bypassConfirmed: boolean;
};

export type ServiceFormFieldError = {
  field: "photoUrls";
  fieldId: string;
  message: string;
};

export type ServiceFormSubmitError = Error & {
  fieldErrors?: ServiceFormFieldError[];
};

export function ServiceFormFill({
  template,
  defaultSubmitterName,
  getUploadUrl,
  submit,
  onSubmitted,
  embedded = false,
  footerExtra,
  testIdPrefix = "",
}: {
  template: ServiceFormTemplate;
  defaultSubmitterName?: string | null;
  /** Requests a presigned upload URL from the caller's endpoint (token- or task-scoped). */
  getUploadUrl: () => Promise<{ uploadUrl: string; objectPath: string }>;
  /** Posts the payload to the caller's submit endpoint; throw Error(message) on failure. */
  submit: (payload: ServiceFormSubmitPayload) => Promise<unknown>;
  onSubmitted: () => void;
  /**
   * embedded=true renders the submit section as the last card (for the portal
   * task detail); embedded=false renders the public page's fixed bottom bar.
   */
  embedded?: boolean;
  /** Extra button(s) rendered beside Submit in the page-variant footer. */
  footerExtra?: React.ReactNode;
  testIdPrefix?: string;
}) {
  const [submitterName, setSubmitterName] = useState("");
  const [sectionNotes, setSectionNotes] = useState<Record<string, string>>({});
  const [additionalNotes, setAdditionalNotes] = useState("");
  const [dateOfService, setDateOfService] = useState(new Date().toISOString().split("T")[0]);
  const [nextServiceDate, setNextServiceDate] = useState("");
  const [checklistResponses, setChecklistResponses] = useState<Record<string, boolean>>({});
  const [photoUrls, setPhotoUrls] = useState<Record<string, string[]>>({});
  const [uploadStatus, setUploadStatus] = useState<Record<string, { done: number; total: number; progress: number }>>({});
  // Uploaded object paths (/objects/...) are private and not servable back to
  // the submitter, so previews render from local blob URLs keyed by the stored
  // path instead.
  const [localPreviews, setLocalPreviews] = useState<Record<string, string>>({});
  const localPreviewsRef = useRef<Record<string, string>>({});
  const [geolocation, setGeolocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [geoError, setGeoError] = useState("");
  const [capturedGeo, setCapturedGeo] = useState(false);
  const [bypassChecked, setBypassChecked] = useState(false);
  const [bypassConfirmed, setBypassConfirmed] = useState(false);
  const [showBypassDialog, setShowBypassDialog] = useState(false);
  const [missingRequiredSlots, setMissingRequiredSlots] = useState<{ id: string; label: string }[]>([]);

  const submitMutation = useMutation({
    mutationFn: async () => {
      const parts: string[] = [];
      for (const section of (template.sections || []) as FormSection[]) {
        if (section.hasNotes && sectionNotes[section.id]) {
          parts.push(`[${section.title}]: ${sectionNotes[section.id]}`);
        }
      }
      if (additionalNotes) parts.push(additionalNotes);
      return submit({
        submitterName,
        notes: parts.join("\n\n"),
        checklistResponses,
        photoUrls,
        latitude: geolocation?.latitude ?? null,
        longitude: geolocation?.longitude ?? null,
        dateOfService,
        nextServiceDate,
        bypassConfirmed,
      });
    },
    onSuccess: () => {
      onSubmitted();
    },
    onError: (error) => {
      const fieldErrors = (error as ServiceFormSubmitError).fieldErrors ?? [];
      const photoErrors = fieldErrors
        .filter((fieldError) => fieldError.field === "photoUrls")
        .map((fieldError) => ({ id: fieldError.fieldId, label: fieldError.message.replace(/^Photo required:\s*"?|"?$/g, "") }));
      if (photoErrors.length > 0) setMissingRequiredSlots(photoErrors);
    },
  });

  const captureGeolocation = () => {
    if (!navigator.geolocation) {
      setGeoError("Geolocation is not supported by your browser");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeolocation({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
        setCapturedGeo(true);
        setGeoError("");
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          setGeoError("Location access was denied. Please allow location access in your browser settings and try again.");
        } else if (err.code === err.POSITION_UNAVAILABLE) {
          setGeoError("Your location is currently unavailable. Please check your device's location settings and try again.");
        } else if (err.code === err.TIMEOUT) {
          setGeoError("Location request timed out. Please try again.");
        } else {
          setGeoError("Could not get your location. Please enable location access and try again.");
        }
      }
    );
  };

  useEffect(() => {
    captureGeolocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Upload a single file straight to object storage via a presigned URL so the
  // browser can report true byte-level progress (mirrors booking-request.tsx).
  // Returns the private object path to store in photoUrls.
  const uploadOneFile = async (file: File, onProgress: (pct: number) => void): Promise<string> => {
    const { uploadUrl, objectPath } = await getUploadUrl();
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", uploadUrl);
      // Extension-aware: a .heic pick often reports an EMPTY type, and the
      // server's IMAGE_MIME allow-list then deletes the object outright —
      // the cleaner sees a green submit and the host sees an empty photo slot.
      xhr.setRequestHeader("Content-Type", uploadMimeType(file, "application/octet-stream"));
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) { onProgress(100); resolve(); }
        else reject(new Error(`Upload failed (${xhr.status})`));
      };
      xhr.onerror = () => reject(new Error("Upload network error"));
      xhr.send(file);
    });
    return objectPath;
  };

  const setPreview = (path: string, file: Blob) => {
    localPreviewsRef.current[path] = URL.createObjectURL(file);
    setLocalPreviews({ ...localPreviewsRef.current });
  };

  const dropPreview = (path: string) => {
    const blobUrl = localPreviewsRef.current[path];
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
      delete localPreviewsRef.current[path];
      setLocalPreviews({ ...localPreviewsRef.current });
    }
  };

  useEffect(() => () => {
    Object.values(localPreviewsRef.current).forEach((u) => URL.revokeObjectURL(u));
  }, []);

  const handleFileUpload = async (files: File[], slotId: string) => {
    if (files.length === 0) return;
    // Track the whole batch so the buttons stay disabled until every selected
    // file finishes. If a batch is already in flight for this slot, fold the
    // new files into the existing total.
    setUploadStatus((prev) => {
      const cur = prev[slotId];
      return {
        ...prev,
        [slotId]: { done: cur?.done || 0, total: (cur?.total || 0) + files.length, progress: 0 },
      };
    });
    // Upload sequentially so the per-slot percentage reflects the file currently
    // in flight while the "X of Y" count advances through the batch.
    for (const file of files) {
      try {
        const objectPath = await uploadOneFile(file, (pct) => {
          setUploadStatus((prev) => {
            const cur = prev[slotId];
            if (!cur) return prev;
            return { ...prev, [slotId]: { ...cur, progress: pct } };
          });
        });
        setPreview(objectPath, file);
        setPhotoUrls((prev) => ({
          ...prev,
          [slotId]: [...(prev[slotId] || []), objectPath],
        }));
      } catch {
        // swallow per-file errors; the batch tracker still advances below
      } finally {
        setUploadStatus((prev) => {
          const cur = prev[slotId];
          if (!cur) return prev;
          const done = cur.done + 1;
          if (done >= cur.total) {
            const { [slotId]: _removed, ...rest } = prev;
            return rest;
          }
          return { ...prev, [slotId]: { ...cur, done, progress: 0 } };
        });
      }
    }
  };

  const removePhoto = (slotId: string, index: number) => {
    setPhotoUrls((prev) => {
      const removed = (prev[slotId] || [])[index];
      if (removed) dropPreview(removed);
      return {
        ...prev,
        [slotId]: (prev[slotId] || []).filter((_, i) => i !== index),
      };
    });
  };

  const openPhotoPicker = (slotId: string, useCamera: boolean) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    if (useCamera) {
      input.capture = "environment";
    } else {
      input.multiple = true;
    }
    input.onchange = (e) => {
      const files = (e.target as HTMLInputElement).files;
      if (files) {
        handleFileUpload(Array.from(files), slotId);
      }
    };
    input.click();
  };

  const toggleChecklist = (itemId: string) => {
    setChecklistResponses((prev) => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const sections = (template.sections || []) as FormSection[];
  const isLandscaping = template.formType === "landscaping";
  const anyUploading = Object.keys(uploadStatus).length > 0;

  const handleSubmit = () => {
    const missing: { id: string; label: string }[] = [];
    for (const section of sections) {
      for (const slot of section.photoSlots) {
        if (slot.required && (!photoUrls[slot.id] || photoUrls[slot.id].length === 0)) {
          missing.push({ id: slot.id, label: slot.label });
        }
      }
      for (const item of section.checklistItems) {
        if (item.photoRequired && (!photoUrls[item.id] || photoUrls[item.id].length === 0)) {
          missing.push({ id: item.id, label: item.label });
        }
      }
    }
    if (missing.length > 0) {
      setMissingRequiredSlots(missing);
      return;
    }
    setMissingRequiredSlots([]);
    submitMutation.mutate();
  };

  // Clear photo-required warnings in real time as the submitter uploads the missing photos.
  const visibleMissingSlots = missingRequiredSlots.filter(
    (slot) => !photoUrls[slot.id] || photoUrls[slot.id].length === 0,
  );

  const submitSection = (
    <>
      {submitMutation.isError && (
        <p className="text-sm text-red-500 mb-2">{(submitMutation.error as Error)?.message}</p>
      )}
      {visibleMissingSlots.length > 0 && (
        <div className="mb-2 space-y-0.5" data-testid="missing-required-slots-error">
          {visibleMissingSlots.map((slot) => (
            <p key={slot.id} className="text-sm text-red-500 flex items-center gap-1">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
              Photo required: {slot.label}
            </p>
          ))}
        </div>
      )}
      {!capturedGeo && !bypassConfirmed && (
        <p className="text-sm text-amber-600 mb-2 flex items-center gap-1">
          <MapPin className="h-3.5 w-3.5" />
          Location is required. Please enable location access above.
        </p>
      )}
      <div className="flex gap-2">
        <Button
          className="flex-1 bg-brand hover:bg-brand-hover text-white"
          onClick={handleSubmit}
          disabled={submitMutation.isPending || anyUploading || (!capturedGeo && !bypassConfirmed)}
          data-testid={`${testIdPrefix}button-submit-form`}
        >
          {submitMutation.isPending ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Submitting...</>
          ) : anyUploading ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading...</>
          ) : (
            <><Upload className="h-4 w-4 mr-2" />Submit Form</>
          )}
        </Button>
        {footerExtra}
      </div>
      <p className="text-xs text-gray-400 text-center mt-2">
        By submitting you confirm all information is accurate
      </p>
    </>
  );

  return (
    <div className={embedded ? "space-y-4" : "space-y-4"}>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Your Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label htmlFor="submitter-name">Your Name</Label>
            <Input
              id="submitter-name"
              value={submitterName}
              onChange={(e) => setSubmitterName(e.target.value)}
              placeholder={defaultSubmitterName || "Enter your name"}
              defaultValue={defaultSubmitterName || ""}
              data-testid={`${testIdPrefix}input-submitter-name`}
            />
          </div>
          <div>
            <Label htmlFor="date-of-service">Date of Service</Label>
            <Input
              id="date-of-service"
              type="date"
              value={dateOfService}
              onChange={(e) => setDateOfService(e.target.value)}
              data-testid={`${testIdPrefix}input-date-of-service`}
            />
          </div>
          {isLandscaping && (
            <div>
              <Label htmlFor="next-service-date">Date of Next Service</Label>
              <Input
                id="next-service-date"
                type="date"
                value={nextServiceDate}
                onChange={(e) => setNextServiceDate(e.target.value)}
                data-testid={`${testIdPrefix}input-next-service-date`}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {sections.map((section) => (
        <Card key={section.id} data-testid={`section-${section.id}`}>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{section.title}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {section.checklistItems.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Checklist</p>
                {section.checklistItems.map((item) => (
                  <label
                    key={item.id}
                    className="flex items-start gap-3 cursor-pointer py-1"
                    data-testid={`checklist-item-${item.id}`}
                  >
                    <Checkbox
                      checked={!!checklistResponses[item.id]}
                      onCheckedChange={() => toggleChecklist(item.id)}
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <span className="text-sm text-gray-700">{item.label}</span>
                      {item.photoRequired && (
                        <Badge variant="secondary" className="ml-2 text-[10px]">Photo required</Badge>
                      )}
                      {item.photoRequired && (
                        <div className="mt-2" data-testid={`checklist-photo-${item.id}`}>
                          {(photoUrls[item.id] || []).length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-2">
                              {(photoUrls[item.id] || []).map((url, idx) => (
                                <div key={url} className="relative">
                                  <img
                                    src={localPreviews[url] || url}
                                    alt={`Photo ${idx + 1}`}
                                    className="w-20 h-20 object-cover rounded border"
                                    data-testid={`photo-preview-${item.id}-${idx}`}
                                  />
                                  <button
                                    type="button"
                                    onClick={(event) => {
                                      event.preventDefault();
                                      removePhoto(item.id, idx);
                                    }}
                                    className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center"
                                    data-testid={`button-remove-photo-${item.id}-${idx}`}
                                  >
                                    <X className="h-2.5 w-2.5" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="flex-1 border-dashed"
                              disabled={!!uploadStatus[item.id]}
                              onClick={(event) => {
                                event.preventDefault();
                                openPhotoPicker(item.id, true);
                              }}
                              data-testid={`button-take-photo-${item.id}`}
                            >
                              {uploadStatus[item.id]
                                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading…</>
                                : <><Camera className="h-4 w-4 mr-2" />Take a photo</>}
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="flex-1 border-dashed"
                              disabled={!!uploadStatus[item.id]}
                              onClick={(event) => {
                                event.preventDefault();
                                openPhotoPicker(item.id, false);
                              }}
                              data-testid={`button-upload-photo-${item.id}`}
                            >
                              {uploadStatus[item.id]
                                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading…</>
                                : <><Upload className="h-4 w-4 mr-2" />Upload photo</>}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            )}

            {section.photoSlots.length > 0 && (
              <div className="space-y-3">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Photos</p>
                {section.photoSlots.map((slot) => {
                  const slotPhotos = photoUrls[slot.id] || [];
                  const slotStatus = uploadStatus[slot.id];
                  const slotBusy = !!slotStatus;
                  const uploadingLabel = slotStatus
                    ? slotStatus.total > 1
                      ? `Uploading ${Math.min(slotStatus.done + 1, slotStatus.total)} of ${slotStatus.total}… ${slotStatus.progress}%`
                      : `Uploading… ${slotStatus.progress}%`
                    : "";
                  return (
                    <div key={slot.id} data-testid={`photo-slot-${slot.id}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <p className="text-sm font-medium text-gray-700">{slot.label}</p>
                        {slot.required && <Badge variant="secondary" className="text-[10px]">Required</Badge>}
                      </div>
                      {slotPhotos.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-2">
                          {slotPhotos.map((url, idx) => (
                            <div key={idx} className="relative">
                              <img
                                src={localPreviews[url] || url}
                                alt={`Photo ${idx + 1}`}
                                className="w-20 h-20 object-cover rounded border"
                                data-testid={`photo-preview-${slot.id}-${idx}`}
                              />
                              <button
                                type="button"
                                onClick={() => removePhoto(slot.id, idx)}
                                className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center"
                                data-testid={`button-remove-photo-${slot.id}-${idx}`}
                              >
                                <X className="h-2.5 w-2.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="flex-1 border-dashed"
                          disabled={slotBusy}
                          onClick={() => openPhotoPicker(slot.id, true)}
                          data-testid={`button-take-photo-${slot.id}`}
                        >
                          {slotBusy ? (
                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{uploadingLabel}</>
                          ) : (
                            <><Camera className="h-4 w-4 mr-2" />Take a photo</>
                          )}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="flex-1 border-dashed"
                          disabled={slotBusy}
                          onClick={() => openPhotoPicker(slot.id, false)}
                          data-testid={`button-upload-photo-${slot.id}`}
                        >
                          {slotBusy ? (
                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{uploadingLabel}</>
                          ) : (
                            <><Upload className="h-4 w-4 mr-2" />Upload photo</>
                          )}
                        </Button>
                      </div>
                      {slotBusy && (
                        <div className="mt-2 h-1 w-full bg-gray-200 rounded overflow-hidden">
                          <div
                            className="h-full bg-brand transition-all"
                            style={{ width: `${slotStatus?.progress ?? 0}%` }}
                            data-testid={`progress-photo-${slot.id}`}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {section.hasNotes && (
              <div>
                <Label>Notes for this section</Label>
                <Textarea
                  placeholder="Add notes, restocking needs, or anything else to report..."
                  value={sectionNotes[section.id] ?? ""}
                  onChange={(e) => setSectionNotes((prev) => ({ ...prev, [section.id]: e.target.value }))}
                  className="mt-1"
                  rows={3}
                  data-testid={`textarea-notes-${section.id}`}
                />
              </div>
            )}
          </CardContent>
        </Card>
      ))}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <MapPin className="h-4 w-4 text-brand" />
            Location
          </CardTitle>
        </CardHeader>
        <CardContent>
          {capturedGeo ? (
            <div className="flex items-center gap-2 text-green-600" data-testid="text-geo-captured">
              <CheckCircle2 className="h-4 w-4" />
              <span className="text-sm">Location captured ({geolocation?.latitude.toFixed(4)}, {geolocation?.longitude.toFixed(4)})</span>
            </div>
          ) : bypassConfirmed ? (
            <div className="flex items-center gap-2 text-amber-600" data-testid="text-geo-bypassed">
              <CheckCircle2 className="h-4 w-4" />
              <span className="text-sm">Location bypassed — service location confirmed by attestation</span>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-gray-600">Your location is required to verify on-site presence. Please allow location access when prompted.</p>
              {geoError && <p className="text-sm text-red-500" data-testid="text-geo-error">{geoError}</p>}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={captureGeolocation}
                data-testid="button-capture-location"
              >
                <MapPin className="h-4 w-4 mr-2" />
                {geoError ? "Retry Location Capture" : "Capturing Location..."}
              </Button>
              {geoError && (
                <div className="pt-2 border-t">
                  <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="label-bypass-location">
                    <input
                      type="checkbox"
                      checked={bypassChecked}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setBypassChecked(checked);
                        if (checked && !bypassConfirmed) {
                          setShowBypassDialog(true);
                        } else if (!checked) {
                          setBypassConfirmed(false);
                        }
                      }}
                      className="rounded"
                      data-testid="checkbox-bypass-location"
                    />
                    <span className="text-gray-600">Bypass Location</span>
                  </label>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showBypassDialog} onOpenChange={(v) => {
        if (!v) {
          setShowBypassDialog(false);
          if (!bypassConfirmed) setBypassChecked(false);
        }
      }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Confirm Location Bypass</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-700 py-2">
            Do you swear under penalty that the service was performed at the location and the date that you have stated on the form?
          </p>
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowBypassDialog(false);
                setBypassChecked(false);
                setBypassConfirmed(false);
              }}
              data-testid="button-bypass-cancel"
            >
              Cancel
            </Button>
            <Button
              className="bg-brand hover:bg-brand-hover text-white"
              onClick={() => {
                setBypassConfirmed(true);
                setShowBypassDialog(false);
              }}
              data-testid="button-bypass-confirm"
            >
              Yes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Additional Notes</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            placeholder="Any additional observations, issues, or notes..."
            value={additionalNotes}
            onChange={(e) => setAdditionalNotes(e.target.value)}
            rows={4}
            data-testid="textarea-additional-notes"
          />
        </CardContent>
      </Card>

      {embedded ? (
        <Card>
          <CardContent className="py-4">
            {submitSection}
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Spacer so the fixed bottom bar never covers the last card. */}
          <div className="h-4" />
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 z-10">
            <div className="max-w-lg mx-auto">
              {submitSection}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
